import { TIMING } from '@konvoi/shared';

/**
 * Bildschirm wachhalten.
 *
 * Geht der Bildschirm aus, endet die Positionsermittlung — das Gerät liegt in
 * der Halterung am Ladekabel und soll deshalb wach bleiben. Die Sperre geht bei
 * jedem Wechsel in den Hintergrund verloren und muss danach neu angefordert
 * werden.
 *
 * Nicht jeder Browser kann das, und nicht jeder darf es: Im Stromsparmodus
 * lehnt Safari ab, und iOS schaltet den unter 20 % Akku von selbst ein und
 * lässt ihn an, bis wieder 80 % erreicht sind. Der Zustand wird deshalb nach
 * außen gegeben und ist in der Oberfläche sichtbar — wer weiß, dass die Sperre
 * nicht greift, legt sein Gerät anders hin.
 */

export type WakeLockState =
  /** Die Sperre wird gehalten. */
  | 'active'
  /** Keine Sperre — Ausgangszustand, oder das Gerät hat sie freigegeben. */
  | 'inactive'
  /** Dieser Browser kennt die Bildschirmsperre nicht. */
  | 'unsupported'
  /**
   * Der Browser hat abgelehnt.
   *
   * Etwas anderes als `inactive`, auch wenn beides dasselbe Ergebnis hat: Hier
   * hilft kein Warten, sondern nur eine Einstellung am Gerät. Die Oberfläche
   * sagt das getrennt — bis zum 18.08.2026 stand für beides derselbe Satz da,
   * und niemand konnte wissen, ob es an der App oder am Gerät lag.
   */
  | 'denied';

export class ScreenWakeLock {
  #sentinel: WakeLockSentinel | null = null;
  #state: WakeLockState = 'inactive';
  /**
   * Der Name des letzten Fehlers.
   *
   * Vorher verschwand er im leeren `catch`. Damit ließ sich hinterher nicht
   * mehr feststellen, warum die Sperre nicht griff — man konnte nur raten.
   */
  #lastError: string | null = null;
  #retry: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly onChange: (state: WakeLockState) => void) {
    if (!('wakeLock' in navigator)) {
      this.#state = 'unsupported';
    }
  }

  get state(): WakeLockState {
    return this.#state;
  }

  /** Woran der letzte Versuch scheiterte — für die Fehlersuche, nicht für den Fahrer. */
  get lastError(): string | null {
    return this.#lastError;
  }

  async request(): Promise<void> {
    if (this.#state === 'unsupported' || this.#sentinel !== null) {
      return;
    }

    try {
      this.#sentinel = await navigator.wakeLock.request('screen');
      this.#lastError = null;
      this.#sentinel.addEventListener('release', () => {
        this.#sentinel = null;
        this.set('inactive');
      });
      this.set('active');
    } catch (fehler) {
      this.#lastError = fehler instanceof Error ? `${fehler.name}: ${fehler.message}` : String(fehler);
      // Nicht still verschlucken: Nach einer Fahrt ist das die einzige Spur.
      console.warn('Bildschirmsperre abgelehnt —', this.#lastError);
      this.set('denied');
      this.planeZweitenVersuch();
    }
  }

  /**
   * Genau ein zweiter Versuch.
   *
   * Der erste fällt mit dem Start der Fahrtansicht zusammen; scheitert er an
   * diesem Zeitpunkt, hilft ein Augenblick Abstand. Lehnt der Browser dagegen
   * grundsätzlich ab, scheitert auch der zweite — und dann bleibt es dabei,
   * statt im Takt weiter anzuklopfen.
   */
  private planeZweitenVersuch(): void {
    if (this.#retry !== null) {
      return;
    }
    this.#retry = setTimeout(() => {
      this.#retry = null;
      if (this.#state === 'denied' && document.visibilityState === 'visible') {
        void this.request();
      }
    }, TIMING.wakeLockRetryMs);
  }

  async release(): Promise<void> {
    if (this.#retry !== null) {
      clearTimeout(this.#retry);
      this.#retry = null;
    }
    if (this.#sentinel !== null) {
      await this.#sentinel.release().catch(() => undefined);
      this.#sentinel = null;
    }
    this.set('inactive');
  }

  /** Nach der Rückkehr in den Vordergrund ist die Sperre weg und wird neu geholt. */
  async reacquire(): Promise<void> {
    if (document.visibilityState === 'visible') {
      await this.request();
    }
  }

  private set(state: WakeLockState): void {
    if (this.#state !== state) {
      this.#state = state;
      this.onChange(state);
    }
  }
}
